import { Details } from '@core/ui';
import { Modal, Row, Spin, Tag, Progress, Collapse, Statistic, List, Typography, Button, message, Tooltip } from 'antd';
import { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import { useService } from '@core/inversify-react';
import { useInspectionReport } from '../hooks';
import { ExportInspectionReportRepoType, IExportInspectionReportRepo } from '../repos';

const { Panel } = Collapse;
const { Text, Title } = Typography;

export const BranchInspectionReportPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [exportLoading, setExportLoading] = useState(false);

  const { data, isFetching } = useInspectionReport(id);
  const exportInspectionReportRepo = useService<IExportInspectionReportRepo>(ExportInspectionReportRepoType);

  const onCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleExportExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      const result = await exportInspectionReportRepo.execute(id);

      if (result.status === 200) {
        // Create download link for the blob
        const url = URL.createObjectURL(result.response);
        const link = document.createElement('a');
        link.href = url;
        link.download = `yoxlanis_${id}_hesabati.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
        message.success('Excel faylı uğurla endirildi');
      } else {
        message.error(result.response);
      }
    } catch (error) {
      message.error('Excel faylı endirilə bilmədi');
    } finally {
      setExportLoading(false);
    }
  }, [id, exportInspectionReportRepo]);

  const completionRate = data ? Math.round((data.correct_parcels_count.value / data.expected_parcels_count.value) * 100) : 0;
  const scanRate = data ? Math.round((data.scanned_parcels_count.value / data.expected_parcels_count.value) * 100) : 0;

  return (
    <Modal
      width={1400}
      visible
      title={`Filial yoxlanışı hesabatı #${id}`}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={onCancel}>Bağla</Button>
          <Button type='primary' icon={<Icons.FileExcelOutlined />} loading={exportLoading} onClick={handleExportExcel}>
            Excel endir
          </Button>
        </div>
      }
    >
      <Spin spinning={isFetching}>
        <Row gutter={[24, 24]}>
          {/* Ümumi Statistika */}
          <Details.Col xs={24}>
            <Details.Card title='Ümumi Nəticə'>
              <Row gutter={[16, 16]}>
                <Details.Col xs={24} sm={12} md={6}>
                  <Statistic title='Gözlənilən' value={data?.expected_parcels_count.value} prefix={<Icons.InboxOutlined />} valueStyle={{ color: '#1890ff' }} />
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    {data?.expected_parcels_count.descr}
                  </Text>
                </Details.Col>
                <Details.Col xs={24} sm={12} md={6}>
                  <Statistic title='Scan edilən' value={data?.scanned_parcels_count.value} prefix={<Icons.ScanOutlined />} valueStyle={{ color: '#722ed1' }} />
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    {data?.scanned_parcels_count.descr}
                  </Text>
                </Details.Col>
                <Details.Col xs={24} sm={12} md={6}>
                  <Statistic title='Düzgün' value={data?.correct_parcels_count.value} prefix={<Icons.CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    {data?.correct_parcels_count.descr}
                  </Text>
                </Details.Col>
                <Details.Col xs={24} sm={12} md={6}>
                  <Statistic title='İcra müddəti' value={data?.duration_minutes.value} suffix='dəqiqə' prefix={<Icons.ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} />
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    {data?.duration_minutes.descr}
                  </Text>
                </Details.Col>
              </Row>
            </Details.Card>
          </Details.Col>

          {/* Proqress */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Tamamlanma Göstəriciləri'>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong>Scan tamamlanma dərəcəsi</Text>
                    <Tooltip title='Hesablama qaydası: (Scan edilən bağlamalar / Gözlənilən bağlamalar) × 100%'>
                      <Icons.QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </div>
                  <Text strong>{scanRate}%</Text>
                </div>
                <Progress
                  percent={scanRate}
                  status={scanRate === 100 ? 'success' : scanRate > 70 ? 'normal' : 'exception'}
                  strokeColor={scanRate === 100 ? '#52c41a' : scanRate > 70 ? '#1890ff' : '#faad14'}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {data?.scanned_parcels_count.value} / {data?.expected_parcels_count.value} bağlama scan edilib
                </Text>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong>Düzgünlük dərəcəsi</Text>
                    <Tooltip title='Hesablama qaydası: (Düzgün scan edilən bağlamalar / Gözlənilən bağlamalar) × 100%'>
                      <Icons.QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </div>
                  <Text strong>{completionRate}%</Text>
                </div>
                <Progress
                  percent={completionRate}
                  status={completionRate === 100 ? 'success' : completionRate > 80 ? 'normal' : 'exception'}
                  strokeColor={completionRate === 100 ? '#52c41a' : completionRate > 80 ? '#1890ff' : '#ff4d4f'}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {data?.correct_parcels_count.value} / {data?.expected_parcels_count.value} bağlama düzgün scan edilib
                </Text>
              </div>
            </Details.Card>
          </Details.Col>

          {/* Vaxt və İcraçı */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='İcra Məlumatları'>
              <Details.Descriptions bordered={true} column={1} size='small'>
                <Details.Descriptions.Item label='İcraçı'>
                  <Tag color='green' icon={<Icons.UserOutlined />}>
                    {data?.executed_by.name}
                  </Tag>
                  <span style={{ marginLeft: 8, color: '#8c8c8c' }}>ID: {data?.executed_by.value}</span>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Başlanma tarixi'>
                  <Icons.PlayCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  {data?.started_at.value}
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Tamamlanma tarixi'>
                  <Icons.CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  {data?.completed_at.value}
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Hesabat tarixi'>
                  <Icons.FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  {data?.created_at.value}
                </Details.Descriptions.Item>
              </Details.Descriptions>
            </Details.Card>
          </Details.Col>

          {/* Problemlər */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Problem Göstəriciləri'>
              <Row gutter={[16, 16]}>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: 16, backgroundColor: '#fff1f0', borderRadius: 8, border: '1px solid #ffa39e' }}>
                    <Statistic title='İtkin bağlamalar' value={data?.missing_parcels_count.value} prefix={<Icons.WarningOutlined />} valueStyle={{ color: '#ff4d4f', fontSize: 28 }} />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {data?.missing_parcels_count.descr}
                    </Text>
                  </div>
                </Details.Col>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: 16, backgroundColor: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                    <Statistic title='Artıq bağlamalar' value={data?.extra_parcels_count.value} prefix={<Icons.ExclamationCircleOutlined />} valueStyle={{ color: '#fa8c16', fontSize: 28 }} />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {data?.extra_parcels_count.descr}
                    </Text>
                  </div>
                </Details.Col>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: 16, backgroundColor: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff' }}>
                    <Statistic title='Başqa filialın bağlamaları' value={data?.foreign_scanned_parcels_count.value} prefix={<Icons.SwapOutlined />} valueStyle={{ color: '#1890ff', fontSize: 28 }} />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {data?.foreign_scanned_parcels_count.descr}
                    </Text>
                  </div>
                </Details.Col>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: 16, backgroundColor: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                    <Statistic title='Düzgün scan edilənlər' value={data?.correct_parcels_count.value} prefix={<Icons.CheckCircleOutlined />} valueStyle={{ color: '#52c41a', fontSize: 28 }} />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {data?.correct_parcels_count.descr}
                    </Text>
                  </div>
                </Details.Col>
              </Row>
            </Details.Card>
          </Details.Col>

          {/* Xülasə */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Hesabat Xülasəsi'>
              <List size='small'>
                <List.Item>
                  <List.Item.Meta
                    avatar={<Icons.CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />}
                    title={`${data?.correct_parcels_count.value} düzgün bağlama`}
                    description='Filialda olmalı idi və düzgün scan edildi'
                  />
                </List.Item>
                <List.Item>
                  <List.Item.Meta
                    avatar={<Icons.WarningOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />}
                    title={`${data?.missing_parcels_count.value} itkin bağlama`}
                    description='Filialda olmalı idi, lakin tapılmadı'
                  />
                </List.Item>
                <List.Item>
                  <List.Item.Meta
                    avatar={<Icons.ExclamationCircleOutlined style={{ fontSize: 20, color: '#fa8c16' }} />}
                    title={`${data?.extra_parcels_count.value} artıq bağlama`}
                    description='Scan edildi, lakin statusu uyğun deyil'
                  />
                </List.Item>
                <List.Item>
                  <List.Item.Meta
                    avatar={<Icons.SwapOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                    title={`${data?.foreign_scanned_parcels_count.value} yad bağlama`}
                    description='Başqa filialın bağlaması burada tapıldı'
                  />
                </List.Item>
              </List>
            </Details.Card>
          </Details.Col>

          {/* İzləmə Kodları Siyahıları */}
          <Details.Col xs={24}>
            <Details.Card title='Ətraflı Siyahılar'>
              <Collapse accordion>
                {/* İtkin bağlamalar */}
                {data && data.missing_parcels_count.value > 0 && (
                  <Panel
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          <Icons.WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                          <strong>İtkin bağlamalar</strong>
                        </span>
                        <Tag color='error'>{data.missing_parcels_count.value} ədəd</Tag>
                      </div>
                    }
                    key='missing'
                  >
                    <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
                      {data.missing_parcels_tracks.descr}
                    </Text>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 8,
                        maxHeight: 400,
                        overflowY: 'auto',
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                      }}
                    >
                      {data.missing_parcels_tracks.value.map((track, idx) => (
                        <Tag key={idx} color='error' style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>
                          {track}
                        </Tag>
                      ))}
                    </div>
                  </Panel>
                )}

                {/* Artıq bağlamalar */}
                {data && data.extra_parcels_count.value > 0 && (
                  <Panel
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          <Icons.ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                          <strong>Artıq bağlamalar (Status uyğun deyil)</strong>
                        </span>
                        <Tag color='warning'>{data.extra_parcels_count.value} ədəd</Tag>
                      </div>
                    }
                    key='extra'
                  >
                    <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
                      {data.extra_parcels_tracks.descr}
                    </Text>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 8,
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                      }}
                    >
                      {data.extra_parcels_tracks.value.map((track, idx) => (
                        <Tag key={idx} color='warning' style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>
                          {track}
                        </Tag>
                      ))}
                    </div>
                  </Panel>
                )}

                {/* Başqa filialın bağlamaları */}
                {data && data.foreign_scanned_parcels_count.value > 0 && (
                  <Panel
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          <Icons.SwapOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                          <strong>Başqa filialın bağlamaları</strong>
                        </span>
                        <Tag color='processing'>{data.foreign_scanned_parcels_count.value} ədəd</Tag>
                      </div>
                    }
                    key='foreign'
                  >
                    <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
                      {data.foreign_scanned_parcels_tracks.descr}
                    </Text>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 8,
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                      }}
                    >
                      {data.foreign_scanned_parcels_tracks.value.map((track, idx) => (
                        <Tag key={idx} color='processing' style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>
                          {track}
                        </Tag>
                      ))}
                    </div>
                  </Panel>
                )}
              </Collapse>
            </Details.Card>
          </Details.Col>

          {/* Performans Analizi */}
          <Details.Col xs={24}>
            <Details.Card>
              <Title level={5}>
                <Icons.BarChartOutlined style={{ marginRight: 8 }} />
                Performans Analizi
              </Title>
              <Row gutter={[16, 16]}>
                <Details.Col xs={24} md={8}>
                  <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f0f5ff', borderRadius: 8 }}>
                    <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                      {data ? Math.round((data.scanned_parcels_count.value / data.expected_parcels_count.value) * 100) : 0}%
                    </Title>
                    <Text type='secondary'>Scan tamamlanma nisbəti</Text>
                  </div>
                </Details.Col>
                <Details.Col xs={24} md={8}>
                  <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f6ffed', borderRadius: 8 }}>
                    <Title level={2} style={{ margin: 0, color: '#52c41a' }}>
                      {data ? Math.round((data.correct_parcels_count.value / data.scanned_parcels_count.value) * 100) || 0 : 0}%
                    </Title>
                    <Text type='secondary'>Scan edilənlərdən düzgün olanlar</Text>
                  </div>
                </Details.Col>
                <Details.Col xs={24} md={8}>
                  <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#fff1f0', borderRadius: 8 }}>
                    <Title level={2} style={{ margin: 0, color: '#ff4d4f' }}>
                      {data ? Math.round((data.missing_parcels_count.value / data.expected_parcels_count.value) * 100) : 0}%
                    </Title>
                    <Text type='secondary'>İtkin bağlama nisbəti</Text>
                  </div>
                </Details.Col>
              </Row>
            </Details.Card>
          </Details.Col>

          {/* Tövsiyələr */}
          {data && (data.missing_parcels_count.value > 0 || data.extra_parcels_count.value > 0 || data.foreign_scanned_parcels_count.value > 0) && (
            <Details.Col xs={24}>
              <Details.Card>
                <Title level={5}>
                  <Icons.BulbOutlined style={{ marginRight: 8, color: '#faad14' }} />
                  Tövsiyələr və Diqqət Edilməli Nöqtələr
                </Title>
                <List size='small' bordered>
                  {data.missing_parcels_count.value > 0 && (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Icons.SearchOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />}
                        title={<Text strong>{data.missing_parcels_count.value} bağlama tapılmadı</Text>}
                        description='Bu bağlamaların yerləşməsi araşdırılmalı və tapılmalıdır. Filialda olmalı idilər.'
                      />
                    </List.Item>
                  )}
                  {data.extra_parcels_count.value > 0 && (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Icons.FileSyncOutlined style={{ fontSize: 18, color: '#fa8c16' }} />}
                        title={<Text strong>{data.extra_parcels_count.value} bağlamanın statusu düzgün deyil</Text>}
                        description='Bu bağlamaların statusu yoxlanılmalı və düzəldilməlidir.'
                      />
                    </List.Item>
                  )}
                  {data.foreign_scanned_parcels_count.value > 0 && (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Icons.RetweetOutlined style={{ fontSize: 18, color: '#1890ff' }} />}
                        title={<Text strong>{data.foreign_scanned_parcels_count.value} bağlama başqa filiala aiddir</Text>}
                        description='Bu bağlamalar düzgün filiallarına transfer edilməlidir.'
                      />
                    </List.Item>
                  )}
                </List>
              </Details.Card>
            </Details.Col>
          )}

          {/* Uğur mesajı */}
          {data && data.missing_parcels_count.value === 0 && data.extra_parcels_count.value === 0 && data.foreign_scanned_parcels_count.value === 0 && (
            <Details.Col xs={24}>
              <div
                style={{
                  padding: 24,
                  backgroundColor: '#f6ffed',
                  borderRadius: 8,
                  border: '2px solid #52c41a',
                  textAlign: 'center',
                }}
              >
                <Icons.CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                  Mükəmməl! Heç bir problem aşkar edilmədi
                </Title>
                <Text type='secondary'>Bütün bağlamalar düzgün yerləşməkdədir və statusları uyğundur.</Text>
              </div>
            </Details.Col>
          )}
        </Row>
      </Spin>
    </Modal>
  );
};
